import Sortable from "sortablejs";

import type { IOptionGroupOptionAlloc } from "@/data/commands/catalog/CreateOptionGroupCmd";
import type { IArticle } from "@/data/model/Article";
import type { IOptionGroup } from "@/data/model/OptionGroup";
import { Format } from "@/data/util/Format";
import { Misc } from "@/data/util/Misc";

import { translate } from "@/shared/localization/language";

import { i18n } from "@/portal/Trans";
import { NumberInput } from "@/portal/formControls/NumberInput";
import { AddOptionToGroupWindow } from "@/portal/modal/AddOptionToGroupWindow";

export interface IOptionAllocatorValue {
    options: Array<IOptionGroupOptionAlloc>;
    minOptions?: number;
    maxOptions?: number;
    maxPerOption?: number;
    maxSum?: number;
}

export class OptionAllocator {
    private addWindow?: AddOptionToGroupWindow;

    private $element: JQuery;
    private $preselectionHeader: JQuery;

    private $options: JQuery;
    private optionSortable: any;

    private $minOptionsInput: JQuery;
    private $maxOptionsInput: JQuery;
    private $maxPerOptionInput: JQuery;
    private $maxSumInput: JQuery;

    private $showAdvancedSettings: boolean = false;

    constructor() {
        this.$element = $('<div class="optionallocator" />');

        const addRow = $('<div class="row addrow" />').appendTo(this.$element);
        addRow.append('<div class="col-xl-2 d-none d-xl-block"></div>');
        const scCol = $('<div class="col-xl-6 col-lg-10 col-sm-10" />').appendTo(addRow);
        this.$minOptionsInput = $('<input type="text" name="minoptions" class="form-control" />').appendTo(scCol);
        this.$minOptionsInput.change(() => {
            const minOptions =
                this.$minOptionsInput.val().length === 0 ? undefined : parseInt(this.$minOptionsInput.val());
            const maxOptions =
                this.$maxOptionsInput.val().length === 0 ? undefined : parseInt(this.$maxOptionsInput.val());

            if (minOptions !== undefined && maxOptions !== undefined && minOptions > maxOptions)
                this.$maxOptionsInput.val(minOptions);

            this.checkMaxOptionsVisibility();
        });

        scCol.append('<div class="hint">' + i18n("elements.optionAllocator.mustTotalUpTo") + "</div>");
        this.$maxOptionsInput = $('<input type="text" name="maxoptions" class="form-control" />').appendTo(scCol);
        this.$maxOptionsInput.change(() => {
            const minOptions =
                this.$minOptionsInput.val().length === 0 ? undefined : parseInt(this.$minOptionsInput.val());
            const maxOptions =
                this.$maxOptionsInput.val().length === 0 ? undefined : parseInt(this.$maxOptionsInput.val());

            if (minOptions !== undefined && maxOptions !== undefined && minOptions > maxOptions)
                this.$minOptionsInput.val(maxOptions);

            this.checkMaxOptionsVisibility();
        });

        scCol.append('<div class="hint">' + i18n("elements.optionAllocator.canUpTo") + "</div>");

        const addBtn = $(
            '<div class="col-xl-1 col-lg-2 col-sm-2"><button type="button" class="btn pull-right btn-success"><i class="la la-plus"></i> ' +
                i18n("general.entities.option") +
                "</button></div>"
        )
            .appendTo(addRow)
            .find("> .btn");
        addBtn.click((e) => {
            e.preventDefault();
            this.openAddWindow();
        });

        const headRow = $('<div class="row headrow" />').appendTo(this.$element);
        headRow.append('<div class="col-xl-2 d-none d-xl-block"></div>');

        const scCol1 = $(
            '<div class="col-xl-4 col-lg-6 col-sm-6" style="display: flex; justify-content: space-between;" />'
        ).appendTo(headRow);

        this.$preselectionHeader = $(
            '<div class="hint" style="padding: 10px 0; line-height: 0; align-self: flex-end;">' +
                i18n("elements.optionAllocator.preselection") +
                "</div>"
        ).appendTo(scCol1);

        const maxOptionsContent = $(
            '<div><div class="hint">' + i18n("elements.optionAllocator.maxPerOption") + "</div></div>"
        ).appendTo(scCol1);
        this.$maxPerOptionInput = $('<input type="text" name="maxperoption" class="form-control" />').appendTo(
            maxOptionsContent
        );
        this.$maxPerOptionInput.change(() => this.checkMaxOptionsVisibility());

        const optionRow = $('<div class="row optrow" style="padding-top: 8px;" />').appendTo(this.$element);
        optionRow.append('<div class="col-xl-2 d-none d-xl-block"></div>');

        this.$options = $('<div class="col-xl-7 col-lg-12 col-sm-12 options" />').appendTo(optionRow);

        const footerRow = $('<div class="row footerrow" />').appendTo(this.$element);
        footerRow.append(' <div class="col-xl-2 d-none d-xl-block" />');

        const scCol2 = $('<div class="col-xl-4 col-lg-6 col-sm-6" />').appendTo(footerRow);
        scCol2.append('<div class="hint">' + i18n("elements.optionAllocator.maxSum") + "</div>");
        this.$maxSumInput = $('<input type="text" name="maxsum" class="form-control" />').appendTo(scCol2);

        this.optionSortable = Sortable.create(this.$options[0], {
            handle: ".draghandle",
            ghostClass: "draging",
            animation: 150,
            draggable: ".option",
            onEnd: (evt) => {
                if (evt.newIndex === evt.oldIndex) return;
            },
        });

        this.checkNoData();
    }

    public setValue(optionGroup?: IOptionGroup): OptionAllocator {
        if (optionGroup === undefined) {
            this.$minOptionsInput.val("");
            this.$maxOptionsInput.val("");
            this.$maxPerOptionInput.val("1");
            this.$maxSumInput.val("3");
            this.$options.empty();
        } else {
            this.$minOptionsInput.val(optionGroup.minOptions === undefined ? "" : optionGroup.minOptions);
            this.$maxOptionsInput.val(optionGroup.maxOptions === undefined ? "" : optionGroup.maxOptions);
            this.$maxPerOptionInput.val(optionGroup.maxPerOption === undefined ? "" : optionGroup.maxPerOption);
            this.$maxSumInput.val(optionGroup.maxSum === undefined ? "" : optionGroup.maxSum);

            this.$options.empty();
            for (const i in optionGroup.options.sort((a, b) => a.order - b.order)) {
                this.addOption(
                    optionGroup.options[i].article,
                    optionGroup.options[i].preselected,
                    optionGroup.options[i].price
                );
            }
        }

        this.checkMaxOptionsVisibility();
        this.checkNoData();
        this.setShowAdvancedSettings(optionGroup?.enableAdvancedSettings === true);

        return this;
    }

    public setShowAdvancedSettings(showAdvancedSettings: boolean) {
        this.$showAdvancedSettings = showAdvancedSettings;
        this.$options.find("> .option").each((i, e) => {
            const el = $(e);
            const preselectedCheckBox = el.find('.checkbox > input[type="checkbox"]');
            if (showAdvancedSettings) {
                preselectedCheckBox.show();
            } else {
                preselectedCheckBox.hide();
            }
        });

        if (showAdvancedSettings) {
            this.$preselectionHeader.css("visibility", "visible");
        } else {
            this.$preselectionHeader.css("visibility", "hidden");
        }
    }

    public getValue(): IOptionAllocatorValue {
        const val: IOptionAllocatorValue = {
            options: [],
            minOptions: this.$minOptionsInput.val().length === 0 ? undefined : parseInt(this.$minOptionsInput.val()),
            maxOptions: this.$maxOptionsInput.val().length === 0 ? undefined : parseInt(this.$maxOptionsInput.val()),
            maxPerOption:
                this.$maxPerOptionInput.val().length === 0 ? undefined : parseInt(this.$maxPerOptionInput.val()),
            maxSum: this.$maxSumInput.val().length === 0 ? undefined : parseInt(this.$maxSumInput.val()),
        };

        let orderIt = 0;
        this.$options.find("> .option").each((i, e) => {
            const el = $(e);
            const article = el.data("model") as IArticle;
            const priceCtrl = el.data("pricectrl") as NumberInput;
            const preselectedCheckBox = el.find('.checkbox > input[type="checkbox"]');

            val.options.push({
                articleId: article.id,
                order: orderIt,
                price: priceCtrl.getValue(),
                preselected: preselectedCheckBox.is(":checked"),
            });

            orderIt++;
        });

        return val;
    }

    public validate(val?: IOptionAllocatorValue): boolean {
        let hasError = false;
        val = val!;

        this.$element.find(".is-invalid").removeClass("is-invalid");
        const optionAmount = this.$options.find("> .option").length;

        if (val.options.length === 0) {
            this.$options.addClass("is-invalid");
            hasError = true;
        }

        if (val.minOptions !== undefined && (isNaN(val.minOptions) || val.minOptions > optionAmount)) {
            this.$minOptionsInput.addClass("is-invalid");
            hasError = true;
        }

        if (
            val.maxOptions !== undefined &&
            (isNaN(val.maxOptions) || val.maxOptions <= 0 || val.maxOptions > optionAmount)
        ) {
            this.$maxOptionsInput.addClass("is-invalid");
            hasError = true;
        }

        if (val.maxSum !== undefined && (isNaN(val.maxSum) || val.maxSum <= 0)) {
            this.$maxSumInput.addClass("is-invalid");
            hasError = true;
        }

        if (val.maxPerOption !== undefined && (isNaN(val.maxPerOption) || val.maxPerOption <= 0)) {
            this.$maxPerOptionInput.addClass("is-invalid");
            hasError = true;
        }

        if (
            val.minOptions !== undefined &&
            val.maxPerOption !== undefined &&
            (val.minOptions > val.maxOptions! || val.maxOptions! <= 0)
        ) {
            this.$minOptionsInput.addClass("is-invalid");
            this.$maxOptionsInput.addClass("is-invalid");
            hasError = true;
        }

        if (val.maxOptions !== undefined && val.maxPerOption !== undefined && val.maxSum !== undefined) {
            const s = val.maxOptions * val.maxPerOption;
            if (!isNaN(s) && s < val.maxSum) {
                this.$maxSumInput.addClass("is-invalid");
                hasError = true;
            }
        }

        const preselectedOptionsCount = val.options.filter((o) => o.preselected).length;
        if (preselectedOptionsCount > val.maxOptions!) {
            this.$maxOptionsInput.addClass("is-invalid");
            hasError = true;
        }

        return hasError;
    }

    public validateException(exceptionMessage: string) {
        if (exceptionMessage == "article_min_quantity_greater_than_option_group_max_quantity_exception") {
            this.$maxPerOptionInput.addClass("is-invalid");
        }
    }

    private addOption(article: IArticle, preselected: boolean, price?: number) {
        const opt = $('<div class="option" />').data("model", article).appendTo(this.$options);
        const lbl = $('<label class="preselection checkbox checkbox-lg"><span></span></label>').appendTo(opt);
        const checkBox = $('<input type="checkbox" />').prependTo(lbl);
        //  this.$wholeDayCheck.prop('checked', true);

        checkBox.prop("checked", preselected);

        opt.append(
            '<a class="name" href="/article/' +
                article.id +
                '" target="_blank">' +
                Format.htmlEscape(article.internalName !== undefined ? article.internalName : translate(article.name)) +
                "</a>"
        );

        const pc = $('<div class="price"><label>CHF</label></div>').appendTo(opt);
        const priceInput = $(
            '<input type="text" class="form-control" placeholder="' +
                (article.price === undefined ? "" : Misc.roundCents(article.price).toFixed(2)) +
                '" value="' +
                (price === undefined ? "" : Misc.roundCents(price).toFixed(2)) +
                '" />'
        ).appendTo(pc);

        const ctrl = new NumberInput(priceInput, {
            min: 0,
            step: 0.01,
            decimals: 2,
        });

        opt.data("pricectrl", ctrl);
        $('<div class="remover"><i class="la la-trash"></i></div>')
            .appendTo(opt)
            .click((e) => {
                $(e.currentTarget).parent().remove();
                this.checkMaxOptionsVisibility();
                this.checkNoData();
            });

        $('<div class="draghandle"><i class="la la-bars"></i></div>').appendTo(opt);

        this.setShowAdvancedSettings(this.$showAdvancedSettings);
    }

    private openAddWindow() {
        if (this.addWindow === undefined) {
            this.addWindow = new AddOptionToGroupWindow();
            this.addWindow.onSelected = (articles) => {
                //check if option is already in list
                const val = this.getValue();

                for (const i in articles) {
                    if (val.options.find((c) => c.articleId === articles[i].id) !== undefined) continue;

                    this.addOption(articles[i], false);
                }

                this.checkMaxOptionsVisibility();
                this.checkNoData();
            };
        }

        const articles = new Array<string>();
        this.$options.find("> .option").each((i, e) => {
            articles.push($(e).data("model").id);
        });

        this.addWindow.setAlreadyAddedArticles(articles).show();
    }

    private checkMaxOptionsVisibility() {
        const maxOptions = this.$maxOptionsInput.val().length === 0 ? undefined : parseInt(this.$maxOptionsInput.val());
        const maxPerOption =
            this.$maxPerOptionInput.val().length === 0 ? undefined : parseInt(this.$maxPerOptionInput.val());

        if (
            (maxOptions !== undefined && maxOptions === 1) ||
            (maxPerOption !== undefined && maxPerOption === 1) ||
            this.$options.find("> .option").length === 1
        ) {
            this.$maxSumInput.parent().hide();
            this.$maxSumInput.val("");
        } else this.$maxSumInput.parent().show();
    }

    private checkNoData() {
        const cont = this.$options.find("> .nodata");

        if (this.$options.find("> .option").length === 0) {
            if (cont.length === 0)
                this.$options.append(
                    '<div class="nodata">' + i18n("elements.optionAllocator.noOptionsSelected") + "</div>"
                );
        } else {
            if (cont.length !== 0) cont.remove();
        }
    }

    public renderTo($element: JQuery): OptionAllocator {
        this.$element.appendTo($element);

        return this;
    }
}
